import * as React from "react";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import type { FieldConfig } from "../types";
import { useLingui } from "@lingui/react/macro";
import { t } from "@lingui/macro";

interface BooleanToggleFieldProps<T> {
  field: FieldConfig<T>;
  value: boolean | null | undefined;
  onChange: (value: boolean) => void;
  onBlur: () => void;
  disabled?: boolean;
  id: string;
  "aria-describedby"?: string;
}

export function BooleanToggleField<T>({
  field,
  value,
  onChange,
  onBlur,
  disabled,
  id,
  "aria-describedby": ariaDescribedBy,
}: BooleanToggleFieldProps<T>) {
  const { i18n } = useLingui();

  const yesLabel = i18n._(t({ id: 'common.yes', message: 'Yes' }));
  const noLabel = i18n._(t({ id: 'common.no', message: 'No' }));

  const [pressedButton, setPressedButton] = React.useState<string | null>(null);

  // The press animation is driven by a timer, so it has to be cancelled on
  // unmount: otherwise a click immediately before the field goes away leaves a
  // timer that fires into a torn-down tree.
  const pressTimerRef = React.useRef<ReturnType<typeof setTimeout> | null>(null);

  React.useEffect(
    () => () => {
      if (pressTimerRef.current !== null) {
        clearTimeout(pressTimerRef.current);
      }
    },
    [],
  );

  const handleButtonClick = React.useCallback((newValue: boolean, buttonId: string) => {
    setPressedButton(buttonId);
    onChange(newValue);
    onBlur();
    // Clear a pending timer first, or a fast second click gets its animation
    // cut short by the previous click's timer.
    if (pressTimerRef.current !== null) {
      clearTimeout(pressTimerRef.current);
    }
    pressTimerRef.current = setTimeout(() => {
      pressTimerRef.current = null;
      setPressedButton(null);
    }, 150);
  }, [onChange, onBlur]);

  return (
    <div
      className="flex gap-2"
      role="group"
      aria-labelledby={`${id}-label`}
      aria-describedby={ariaDescribedBy}
      data-testid={field.testid}
    >
      <Button
        id={`${id}-yes`}
        type="button"
        variant={value === true ? "default" : "outline"}
        onClick={() => {
          if (value === true) return;
          handleButtonClick(true, `${id}-yes`);
        }}
        disabled={disabled}
        className={cn(
          "flex-1 transition-all duration-150 ease-in-out",
          value === true && "font-semibold",
          pressedButton === `${id}-yes` && "scale-[0.98]"
        )}
        aria-pressed={value === true}
      >
        {yesLabel}
      </Button>
      <Button
        id={`${id}-no`}
        type="button"
        variant={value === false ? "destructive" : "outline"}
        onClick={() => {
          if (value === false) return;
          handleButtonClick(false, `${id}-no`);
        }}
        disabled={disabled}
        className={cn(
          "flex-1 transition-all duration-150 ease-in-out",
          value === false && "font-semibold",
          pressedButton === `${id}-no` && "scale-[0.98]"
        )}
        aria-pressed={value === false}
      >
        {noLabel}
      </Button>
    </div>
  );
}